import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Box, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import Grid from '@material-ui/core/Grid'
import ConsumerCheckoutPlaceOrder from './ConsumerCheckoutPlaceOrder'
import CheckoutErrorAlert, { ReturnFailureAlert } from './CheckoutErrorAlert'
import { pushToDataLayer } from '../../../utils/dataLayer'
import FormField from '../../Forms/FormField'
import SelfGiftingCheckoutError from '../../Messages/SelfGiftingCheckoutError'
import PhoneNumber from './PhoneNumber'
import isEmpty from 'lodash/isEmpty'
import { FUNDING, PayPalButtons } from '@paypal/react-paypal-js'
import { MASKED_PHONE_NUMBER_CHAR_COUNT } from '../../../constants'
import { postValidateRecipientEmail } from '../../../api'
import { ERROR_INVALID_RECIPIENT_EMAIL } from '../../../errorConstants'

import { ContactDetailsElement } from '@stripe/react-stripe-js/checkout'
import CheckoutSessionPaymentFields from './CheckoutSessionPaymentFields'
import PayPalAccordionRow from './PayPalAccordionRow'
import { validateActiveCheckoutElements } from '../checkoutSessionRuntime'
import { Element, scroller } from 'react-scroll'
import { removeErrorFromPath } from '../../../utils/formErrorUtils'

const ZERO_BOUNCE_ERROR_MESSAGE = ERROR_INVALID_RECIPIENT_EMAIL
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const useStyles = makeStyles((theme) => ({
    customerInfoPaymentMethodContainer: {
        maxWidth: 500,
        margin: '0 auto',
        top: 0,
        [theme.breakpoints.up('md')]: {
            paddingRight: 40,
            position: 'sticky',
            maxWidth: 'none',
        },
    },
    paymentMethodOption: {
        fontSize: 14,
    },
    payPalButton: {
        display: 'block',
        margin: '0 0 0.75rem 0px',
        borderRadius: 60,
        border: '0px solid',
        height: 50,
        width: '100%',
        backgroundColor: '#FFC43A',
        overflow: 'hidden',
        [theme.breakpoints.up('md')]: {
            maxWidth: 555,
        },
    },
    logInBox: {
        padding: '10px',
    },
    logIn: {
        fontSize: '1rem',
        fontWeight: 'bold',
        color: theme.palette.primary.main,
        textTransform: 'none',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
    },
    h2: {
        fontSize: '1.27rem',
        fontWeight: 'bold',
        marginTop: '1.5rem',
        marginBottom: '0.2rem',
        paddingBottom: 14,
        color: theme.palette.primary.main,
    },
    h4: {
        fontSize: '1rem',
        fontWeight: 'bold',
        marginBottom: '1rem',
        color: theme.palette.primary.main,
    },
    sectionHeading: {
        marginBottom: '0.75rem',
    },
    paymentMethodHeading: {
        display: 'flex',
        alignItems: 'center',
    },
    paymentMethodContainer: {
        minWidth: 300,
        backgroundColor: 'white',
        position: 'unset', // prevent stacking context so it doesn't show in front of processing backdrop
    },
    paymentMethodIcon: {
        fontSize: '1.9rem',
        marginLeft: '8px',
    },
    creditCardInfoContainer: {
        marginTop: '1rem',
    },
    paymentFields: {
        marginBottom: '0rem',
        minWidth: '269px',
        padding: '8px 0px 0px 0px',
        fontSize: '1rem',
        outline: 0,
        borderRadius: '4px',
        border: 'none',
        background: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        margin: 0,
        [theme.breakpoints.up('md')]: {
            maxWidth: 555,
        },
        '&:focused': {
            boxShadow:
                'rgba(50, 50, 93, 0.109804) 0 4px 6px, rgba(0, 0, 0, 0.0784314) 0 1px 3px',
            '-webkit-transition': 'all 150ms ease',
            transition: 'all 150ms ease',
        },
        '&:last-of-type': {
            '& label': {
                minWidth: '42px',
                [theme.breakpoints.up('sm')]: {
                    minWidth: '90px',
                },
            },
        },
        [theme.breakpoints.up('sm')]: {},
    },
    blue: {
        color: '#0099FF',
    },
    explanationText: {
        fontSize: '0.8rem',
        padding: '0 1.4rem',
    },
    emailField: {
        padding: 0,
        width: '100%',
        fontSize: '1rem',
        border: 'none',
        boxSizing: 'border-box',
        [theme.breakpoints.up('md')]: {
            maxWidth: 555,
            padding: '0',
        },
    },
    paymentFormSeperator: {
        margin: '31px 0',
        backgroundColor: '#e7e7e7',
        border: '0',
        height: 2,
        [theme.breakpoints.up('md')]: {
            maxWidth: 555,
        },
    },
    paymentMethodOptionContainer: {
        width: '100%',
    },
    cardElementContainer: {
        display: 'block',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1.5rem',
        width: '100%',
        position: 'relative',
    },
    cardElement: {
        flex: 1,
        display: 'block',
        padding: '28px 23px',
        fontSize: '36px',
        fontFamily: 'Source Code Pro, monospace',
        boxShadow:
            'rgba(50, 50, 93, 0.14902) 0 1px 3px, rgba(0, 0, 0, 0.0196078) 0 1px 0',
        border: '2.5px solid #8a8a8a',
        outline: 0,
        borderRadius: '60px',
        height: '64px',
        '&:focused': {
            boxShadow:
                'rgba(50, 50, 93, 0.109804) 0 4px 6px, rgba(0, 0, 0, 0.0784314) 0 1px 3px',
            '-webkit-transition': 'all 150ms ease',
            transition: 'all 150ms ease',
        },
        '&::placeholder': {
            color: '#aab7c4',
        },
        '& label': {
            marginTop: '10px',
        },
    },
    cardElementLabel: {
        position: 'absolute',
        left: '25px',
        top: '50%',
        transform: 'translateY(-50%)',
        transition: 'all 0.2s ease',
        pointerEvents: 'none',
    },
    labelFloating: {
        top: '15px',
        left: '25px',
        fontSize: '0.8em',
        color: '#999',
    },
    closeButton: {
        position: 'absolute',
        color: 'white',
        backgroundColor: '#6f6f6f',
        right: 0,
        padding: 0,
    },
}))

const ConsumerGuestCheckoutForm = (props) => {
    const {
        baseUrl,
        handleShowLogin,
        isPaypalCheckout,
        handlePaypalOnClick,
        handlePaypalCreateOrder,
        handlePaypalOnApprove,
        handlePaypalOnError,
        handlePaypalOnCancel,
        showCreditCardForm,
        billingDetails,
        setBillingDetails,
        setSelectedPaymentElement,
        onPaymentFormLoadError,
        checkoutSessionReady,
        checkoutSessionLoadFailed,
        errors,
        errorCode,
        billingErrors,
        cvv,
        setCvv,
        processing,
        setProcessing,
        validateField,
        handlePlaceOrder,
        showPlaceOrderButton,
        cartHasSelfGiftingVirtualVisaWithTotalBalanceMoreThanThreshold,
        cartHasVirtualVisaWithTotalBalanceMoreThanThreshold,
        errorSelfGifting,
        errorPhone,
        errorCvv,
        handleGuestSignup,
        validateCreditCardForm,
        setBillingErrors,
        setErrorPhone,
        setErrorCvv,
        showPhoneInput,
        checkingPhone,
        setCheckingPhone,
        phoneCheckSuccess,
        setPhoneCheckSuccess,
        clearErrors,
        options,
        showStripe,
        handleErrorAlertClick,
        showCardError,
        setShowCardError,
        returnFailureMessage,
        onDismissReturnFailure,
        setErrors,
    } = props
    // CON-9505: the billing address element reports its values through onChange; unlike the classic
    // AddressElement there is no getValue() to read them back at submit time.
    const [isPayPalOpen, setIsPayPalOpen] = useState(false)
    // Reported to the fields component, which owns the gate; nothing out here reads it.
    const [, setStripeElementReady] = useState(false)
    const paymentFieldsRef = useRef(null)
    const formRef = useRef()
    const classes = useStyles()
    const [canHandlePlaceOrder, setCanHandlePlaceOrder] = useState(false)
    const [paypalButtonRefresher, setPaypalButtonRefresher] = useState(true)
    const [previousPhoneNumber, setPreviousPhoneNumber] = useState('')
    const emailRef = useRef(billingDetails?.email || '')
    const [lastValidatedEmail, setLastValidatedEmail] = useState(null)
    const [lastZeroBounceResult, setLastZeroBounceResult] = useState(null)

    const scrollerConfig = useMemo(
        () => ({
            duration: 0,
            delay: 0,
            smooth: false,
            offset: -120,
        }),
        []
    )

    useEffect(() => {
        emailRef.current = billingDetails?.email || ''
    }, [billingDetails.email])

    const removeError = (path) => {
        setBillingErrors(removeErrorFromPath(path, billingErrors))
    }

    const setEmailErrorMessages = (messages) => {
        setBillingErrors((prev) => {
            const currentErrors = prev || {}

            if (!messages || messages.length === 0) {
                if (!currentErrors.email) {
                    return prev
                }

                const { email: _removedEmail, ...rest } = currentErrors
                return Object.keys(rest).length ? rest : {}
            }

            return {
                ...currentErrors,
                email: messages,
            }
        })
    }

    const setEmailFormatError = (message) => {
        setEmailErrorMessages([message])
    }

    const clearZeroBounceEmailError = () => {
        setBillingErrors((prev) => {
            const currentErrors = prev || {}

            if (!currentErrors.email) {
                return prev
            }

            const filteredMessages = currentErrors.email.filter(
                (message) => message !== ZERO_BOUNCE_ERROR_MESSAGE
            )

            if (filteredMessages.length === currentErrors.email.length) {
                return prev
            }

            if (filteredMessages.length === 0) {
                const { email: _removedEmail, ...rest } = currentErrors
                return Object.keys(rest).length ? rest : {}
            }

            return {
                ...currentErrors,
                email: filteredMessages,
            }
        })
    }

    const clearEmailFormatErrors = () => {
        setBillingErrors((prev) => {
            const currentErrors = prev || {}

            if (!currentErrors.email) {
                return prev
            }

            const zeroBounceMessages = currentErrors.email.filter(
                (message) => message === ZERO_BOUNCE_ERROR_MESSAGE
            )

            if (zeroBounceMessages.length === currentErrors.email.length) {
                return prev
            }

            if (zeroBounceMessages.length === 0) {
                const { email: _removedEmail, ...rest } = currentErrors
                return Object.keys(rest).length ? rest : {}
            }

            return {
                ...currentErrors,
                email: zeroBounceMessages,
            }
        })
    }

    const applyZeroBounceEmailError = (message) => {
        setEmailErrorMessages([message])
    }

    const validateBillingEmailWithZeroBounce = async (
        emailToValidate,
        { force = false } = {}
    ) => {
        const normalizedEmail = (emailToValidate || '').trim()

        if (!normalizedEmail) {
            return { block: false }
        }

        if (
            !force &&
            lastValidatedEmail === normalizedEmail &&
            lastZeroBounceResult !== null
        ) {
            return lastZeroBounceResult
        }

        try {
            const response = await postValidateRecipientEmail({
                email: normalizedEmail,
            })

            const block = Boolean(response?.block)
            const message =
                response && typeof response.message === 'string'
                    ? response.message
                    : ZERO_BOUNCE_ERROR_MESSAGE

            const normalizedResult = {
                block,
                status: response?.status ?? null,
                sub_status: response?.sub_status ?? null,
                message,
                error: Boolean(response?.error),
            }

            if ((emailRef.current || '').trim() !== normalizedEmail) {
                return normalizedResult
            }

            setLastValidatedEmail(normalizedEmail)
            setLastZeroBounceResult(normalizedResult)

            if (block) {
                applyZeroBounceEmailError(message)
            } else {
                clearZeroBounceEmailError()
            }

            return normalizedResult
        } catch (error) {
            console.error('Error validating billing email', error)
            return { block: false, error: true }
        }
    }

    const validateEmailField = async (
        emailValue,
        { shouldValidateServer = false } = {}
    ) => {
        const normalizedEmail = (emailValue || '').trim()

        if (!normalizedEmail) {
            setEmailFormatError('A valid email address is required.')
            return false
        }

        if (!EMAIL_REGEX.test(normalizedEmail)) {
            setEmailFormatError('Please enter a valid email address.')
            return false
        }

        clearEmailFormatErrors()

        if (shouldValidateServer) {
            await validateBillingEmailWithZeroBounce(normalizedEmail)
        }

        return true
    }

    const handleEmailBlur = () => {
        validateEmailField(emailRef.current || '', {
            shouldValidateServer: true,
        })
    }

    const submitEventListener = async (e) => {
        e.preventDefault()
        // Asked first so Stripe's field errors show in the same click as a phone or email error.
        const elementsAreValid = await validateActiveCheckoutElements()
        let doScrollAndReturn = false
        const phoneRegex = /^\(\d{3}\) \d{3}-\d{4}$/

        if (errorPhone) {
            scroller.scrollTo('emailAndPhoneElement', scrollerConfig)
            return
        }
        if (!phoneRegex.test(billingDetails.phone)) {
            setErrorPhone('Please enter a valid phone number.')
            doScrollAndReturn = true
        } else {
            setErrorPhone('')
        }
        // Email validation check
        const emailIsValid = await validateEmailField(billingDetails.email)
        if (!emailIsValid) {
            doScrollAndReturn = true
        }

        if (doScrollAndReturn) {
            scroller.scrollTo('emailAndPhoneElement', scrollerConfig)
            return
        }

        const normalizedEmail = (billingDetails.email || '').trim()
        const zeroBounceResult = await validateBillingEmailWithZeroBounce(
            normalizedEmail,
            {
                force: true,
            }
        )

        if (zeroBounceResult.block) {
            scroller.scrollTo('emailAndPhoneElement', scrollerConfig)
            return
        }


        pushToDataLayer(
            ['CheckoutClick', 'Checkout', 'Click', 'Submit Order'],
            { type: 'stripe' }
        )

        if (!elementsAreValid) {
            scroller.scrollTo('cardFormElements', scrollerConfig)
            return
        }

        // 2. Handle guest sign up
        console.log(
            'cb92024a ConsumerGuestCheckoutForm submitEventListener calling handleGuestSignup'
        )
        setProcessing(true)
        await handleGuestSignup()

        console.log(
            'cb92024a ConsumerGuestCheckoutForm submitEventListener setting setCanHandlePlaceOrder true'
        )
        setCanHandlePlaceOrder(true)
    }

    useEffect(() => {
        console.log(
            'cb92024a ConsumerGuestCheckoutForm effect wrapping handlePlaceOrder init',
            { canHandlePlaceOrder }
        )
        if (canHandlePlaceOrder) {
            setCanHandlePlaceOrder(false)
            console.log(
                'cb92024a ConsumerGuestCheckoutForm effect wrapping handlePlaceOrder calling handlePlaceOrder',
                { canHandlePlaceOrder }
            )
            handlePlaceOrder(true)
        }
    }, [canHandlePlaceOrder, handlePlaceOrder])

    useEffect(() => {
        // Hack part one. This is a hack to re-render the PayPalButtons component
        // every time billingDetailsChanges. The createOrder function
        // gets cached at the time of render, so this is the only way
        // we've found to accomplish having the phone number in the handlePaypalCreateOrder function.
        // This causes the paypal button to flash, so we only want to update it when the use reaches a full phone number
        setPreviousPhoneNumber(billingDetails.phone)
        if (errorPhone) {
            setPaypalButtonRefresher(false)
            return
        }
        if (
            billingDetails?.phone?.length >= MASKED_PHONE_NUMBER_CHAR_COUNT ||
            (previousPhoneNumber?.length >= MASKED_PHONE_NUMBER_CHAR_COUNT &&
                billingDetails?.phone?.length < MASKED_PHONE_NUMBER_CHAR_COUNT)
        ) {
            setPaypalButtonRefresher(false)
        }
        // eslint-disable-next-line
    }, [billingDetails.phone, errorPhone])

    useEffect(() => {
        // Hack part two. paypalButtonRefresher must be toggled back to true
        // in a separate useEffect.
        setPaypalButtonRefresher(true)
        // eslint-disable-next-line
    }, [paypalButtonRefresher])
    return (
        <Grid
            item
            xs={12}
            md={6}
            className={classes.customerInfoPaymentMethodContainer}
        >
            <Box style={{ marginBottom: 12 }}>
                <Box
                    display={'flex'}
                    flexDirection={'row'}
                    alignItems={'center'}
                    style={{
                        borderStyle: 'solid',
                        borderColor: '#d6d6d6',
                        borderWidth: '0.1rem',
                        padding: '0 1rem',
                        marginBottom: '1rem',
                    }}
                >
                    <Typography className={classes.logInBox}>
                        Already have an account?{' '}
                        <span
                            className={classes.logIn}
                            onClick={() => {
                                handleShowLogin()
                            }}
                        >
                            Log In
                        </span>
                    </Typography>
                </Box>
                <Typography variant="h2" className={classes.h2}>
                    Contact Details
                </Typography>
                <Box
                    display={'flex'}
                    flexDirection={'row'}
                    alignItems={'center'}
                >
                    <Typography variant={'h4'} className={classes.h4}>
                        Add Contact Info
                    </Typography>
                </Box>
                <Element name="emailAndPhoneElement" />
                {(errorPhone || !isEmpty(billingErrors?.email)) && (
                    <Typography variant={'body1'} style={{ color: '#D74242' }}>
                        Please enter all contact information
                    </Typography>
                )}
                {!cartHasVirtualVisaWithTotalBalanceMoreThanThreshold &&
                    checkoutSessionReady && (
                    <fieldset className={classes.emailField}>
                        <ContactDetailsElement
                            onChange={(event) => {
                                setBillingDetails({
                                    ...billingDetails,
                                    email: event.value?.email,
                                })
                            }}
                            onBlur={handleEmailBlur}
                        />
                        {!isEmpty(billingErrors?.email) && (
                            <Typography
                                variant="body2"
                                style={{ color: '#D74242', marginTop: 4 }}
                            >
                                {billingErrors.email[0]}
                            </Typography>
                        )}
                    </fieldset>
                )}
                {showPhoneInput && (
                    <PhoneNumber
                        billingDetails={billingDetails}
                        setBillingDetails={setBillingDetails}
                        errorPhone={errorPhone}
                        setErrorPhone={setErrorPhone}
                        validateOnType={true}
                        checkingPhone={checkingPhone}
                        setCheckingPhone={setCheckingPhone}
                        phoneCheckSuccess={phoneCheckSuccess}
                        setPhoneCheckSuccess={setPhoneCheckSuccess}
                        clearErrors={clearErrors}
                        setShowCardError={setShowCardError}
                        labelColor={'#222'}
                        errorBorderWidth={3}
                        errorFontSize={'.85rem'}
                    />
                )}
                <Typography
                    varient={'body1'}
                    className={`${classes.explanationText}`}
                >
                    Required for account security.
                </Typography>
                <hr className={classes.paymentFormSeperator} />
                <Typography
                    variant="h2"
                    className={`${classes.h2} ${classes.paymentMethodHeading}`}
                >
                    Select payment method
                </Typography>
            </Box>

            <Element name="cardFormElements" />

            {/* Stripe credit card form */}
            {showCreditCardForm && showStripe && (
                <form ref={formRef} onSubmit={submitEventListener}>
                    <fieldset className={classes.paymentFields}>
                        <div className={`${classes.cardElementContainer}`}>
                            <CheckoutSessionPaymentFields
                                ref={paymentFieldsRef}
                                checkoutSessionReady={checkoutSessionReady}
                                checkoutSessionLoadFailed={checkoutSessionLoadFailed}
                                onFocus={() => setShowCardError(false)}
                                onSelectionChange={(selection) =>
                                    setSelectedPaymentElement(
                                        selection?.type ?? ''
                                    )
                                }
                                onLoadError={onPaymentFormLoadError}
                                onInteract={() => setIsPayPalOpen(false)}
                                onElementReady={setStripeElementReady}
                                payPalOpen={isPayPalOpen}
                                payPalRow={
                                    paypalButtonRefresher &&
                                    !cartHasSelfGiftingVirtualVisaWithTotalBalanceMoreThanThreshold ? (
                                        <PayPalAccordionRow
                                            open={isPayPalOpen}
                                            onToggle={(open) => {
                                                setIsPayPalOpen(open)
                                                if (open) {
                                                    // Opening PayPal is a new payment attempt: a decline
                                                    // left over from a card attempt otherwise stays on
                                                    // screen underneath the PayPal button, and the Stripe
                                                    // rows collapse so the list behaves as one accordion.
                                                    clearErrors()
                                                    paymentFieldsRef.current?.collapse()
                                                }
                                            }}
                                        >
                                            <div
                                                className={classes.payPalButton}
                                                data-testid="paypal-checkout-button-container"
                                            >
                                                <PayPalButtons
                                                    style={{ shape: 'pill', height: 55 }}
                                                    fundingSource={FUNDING.PAYPAL}
                                                    onClick={handlePaypalOnClick}
                                                    createOrder={handlePaypalCreateOrder}
                                                    onApprove={handlePaypalOnApprove}
                                                    onError={handlePaypalOnError}
                                                    onCancel={handlePaypalOnCancel}
                                                />
                                            </div>
                                        </PayPalAccordionRow>
                                    ) : null
                                }
                            />
                        </div>
                    </fieldset>
                    <ReturnFailureAlert
                        message={returnFailureMessage}
                        onClose={onDismissReturnFailure}
                    />
                    {/* Separate anchor, not the alert itself: submit scrolls in the same tick the
                        error state is set, before the alert has mounted. */}
                    <Element name="checkoutErrorAlert" />
                    <CheckoutErrorAlert
                        errors={errors}
                        errorCode={errorCode}
                        showCardError={showCardError}
                        handleClick={handleErrorAlertClick}
                    />
                    <SelfGiftingCheckoutError open={errorSelfGifting} />
                    {/* Must be inside form for submit listener */}
                    <hr className={classes.paymentFormSeperator} />
                    <ConsumerCheckoutPlaceOrder
                        diagnosticInfo={{
                            caller: 'Guest, Stripe credit card form',
                            showCreditCardForm,
                            showStripe,
                            showPlaceOrderButton,
                            billingDetails,
                            billingErrors,
                            errorPhone,
                            phoneCheckSuccess,
                            isPaypalCheckout,
                        }}
                        type="submit"
                        baseUrl={baseUrl}
                        processing={processing}
                        error={billingErrors && billingErrors.length > 0}
                        disableSubmit={
                            !showPlaceOrderButton ||
                            errorPhone ||
                            !phoneCheckSuccess
                        }
                        placeOrderButtonText={'Submit Order'}
                        // An open PayPal row makes PayPal's own button the order action, so
                        // the submit button yields (the terms text stays) until a Stripe row
                        // is picked again.
                        isPaypalCheckout={isPaypalCheckout || isPayPalOpen}
                        handlePaypalOnClick={handlePaypalOnClick}
                        handlePaypalCreateOrder={handlePaypalCreateOrder}
                        handlePaypalOnApprove={handlePaypalOnApprove}
                        handlePaypalOnError={handlePaypalOnError}
                        handlePaypalOnCancel={handlePaypalOnCancel}
                        billingDetails={billingDetails}
                        errorPhone={errorPhone}
                        checkingPhone={checkingPhone}
                    />
                </form>
            )}

            {/* Existing payment method, PayPal or Apple/Google Pay in use */}
            {!showCreditCardForm && (
                <>
                    <CheckoutErrorAlert
                        errors={errors}
                        errorCode={errorCode}
                        showCardError={showCardError}
                        handleClick={handleErrorAlertClick}
                    />
                    <SelfGiftingCheckoutError open={errorSelfGifting} />
                    <ConsumerCheckoutPlaceOrder
                        diagnosticInfo={{
                            caller: 'Guest, Existing payment method, PayPal or Apple/Google Pay in use',
                            showCreditCardForm,
                            showStripe,
                            showPlaceOrderButton,
                            billingDetails,
                            billingErrors,
                            errorPhone,
                            phoneCheckSuccess,
                            isPaypalCheckout,
                        }}
                        baseUrl={baseUrl}
                        processing={processing}
                        error={billingErrors && billingErrors.length > 0}
                        disableSubmit={
                            !showPlaceOrderButton ||
                            errorPhone ||
                            !phoneCheckSuccess
                        }
                        onSubmit={async (e) => {
                            pushToDataLayer([
                                'CheckoutClick',
                                'Checkout',
                                'Click',
                                'Submit Order',
                            ])
                            await handlePlaceOrder(e)
                        }}
                        placeOrderButtonText={'Submit Order'}
                        isPaypalCheckout={isPaypalCheckout}
                        handlePaypalOnClick={handlePaypalOnClick}
                        handlePaypalCreateOrder={handlePaypalCreateOrder}
                        handlePaypalOnApprove={handlePaypalOnApprove}
                        handlePaypalOnError={handlePaypalOnError}
                        handlePaypalOnCancel={handlePaypalOnCancel}
                        billingDetails={billingDetails}
                        checkingPhone={checkingPhone}
                        errorPhone={errorPhone}
                    />
                </>
            )}
        </Grid>
    )
}

export default ConsumerGuestCheckoutForm
